import { Component, Injector, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { MatDatepicker } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AbstractComponent, Channel, ChannelService, Department, DepartmentService, ExportService, ModalReportContactComponent, Pager, ReportService, User, UserService } from 'lib-trend-core';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { saveAs } from 'file-saver';

interface ResponseCountItemsContactTypes {
  totalContactCurrent: number;
  totalContactPrevious: number;
  totalContacts: number;
  totalChannels: number;
  totalDepartments: number;
  totalUsers: number;
  totalChannelIds: string[];
  totalDepartmentIds: string[];
  totalUserIds: string[];
}

interface ResponseListItemsContactTypes {
  channel: string;
  department: string;
  user: string;
  totalContactCurrent: number;
  totalContactPrevious: number;
}

interface ResponseGraphicUserTypes {
  totalNewContacts: number;
  attendant: string;
}

interface ResponseGraphicChannelTypes {
  newContacts: number;
  channel: string;
}

interface ResponseGraphicDepartmentTypes {
  newContacts: number;
  department: string;
}

interface ResponseGraphicTypes {
  name: string;
  value: number;
}

@Component({
    selector: 'app-report-list-contact',
    templateUrl: './report-list-contact.v2.component.html',
    // templateUrl: './report-list-contact.component.html',
    styleUrl: './report-list-contact.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ReportListContactComponent extends AbstractComponent implements OnInit {
  title: string;
  @ViewChild('picker', { static: true }) picker: MatDatepicker<Date>;
  reportForm: any;
  userAttendant: User[];
  department: Department[];
  channelAttendant: Channel[];
  countItemsContact: ResponseCountItemsContactTypes;
  graphicChannel: ResponseGraphicTypes[];
  graphicUser: ResponseGraphicTypes[];
  graphicDepartment: ResponseGraphicTypes[];
  pager: Pager<ResponseListItemsContactTypes> = new Pager<ResponseListItemsContactTypes>({ perPage: 10 });
  colorScheme: any = {
    domain: ['#aa98ec', '#4213f6']
  };
  selectedGraphic: string = 'channel';
  selectedGraphicLabel: string = 'report.listContact.chart';
  selectedList: string = 'user';
  selectedListLabel: string = 'report.listContact.list';

  view: [number, number] = [600, 400]

  typeCharts = [
    {
      name: 'report.listContact.channelColumn',
      value: 'channel'
    },
    {
      name: 'report.listContact.attendantColumn',
      value: 'user'
    },
    {
      name: 'report.listContact.departmentColumn',
      value: 'department'
    },
  ];

  labelChannel: string = 'report.listContact.channelColumn';
  labelDepartment: string = 'report.listContact.departmentColumn';
  labelAttendant: string = 'report.listContact.attendantColumn';
  labelGraphic: string = 'report.listContact.chart'

  breadcrumb = [
    { href: '/report', label: 'report.breadcrumb.reports' },
    { label: 'report.breadcrumb.bcNewContactsPerformance' },
  ];

  constructor(
    injetor: Injector,
    private fb: FormBuilder,
    public userSerivce: UserService,
    public departmentService: DepartmentService,
    public channelService: ChannelService,
    public reportService: ReportService,
    public exportService: ExportService,
    public override router: Router,
    public dialog: MatDialog,
  ) {
    super(injetor);
    this.loadingContent = true;
  }

  ngOnInit(): void {
    this.createForm();
    this.getByUserAttendant();
    this.getDepartmentByCompany();
    this.getByAllChannel();
    this.initializeData();

    this.route.queryParams.subscribe(params => {
      this.title = params['title'];
    });

    this.reportForm.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr))
    ).subscribe(val => {
      this.onFormChange(val);
    });
  }

  selectChannel(id: number | string, name: string) {
    this.reportForm.get('channel')!.setValue(id, { emitEvent: true });
    this.labelChannel = name ?? 'report.listContact.channelColumn';
  }

  selectDepartment(id: number | string, name: string) {
    this.reportForm.get('department')!.setValue(id, { emitEvent: true });
    this.labelDepartment = name ?? 'report.listContact.departmentColumn';
  }

  selectAttendant(id: number | string, name: string) {
    this.reportForm.get('attendant')!.setValue(id, { emitEvent: true });
    this.labelAttendant = name ?? 'report.listContact.attendantColumn';
  }

  private initializeData() {
    const formsValue = {
      dateStartCurrent: this.reportForm.value.dateRange.dateStartCurrent,
      dateEndCurrent: this.reportForm.value.dateRange.dateEndCurrent,
      dateStartPrevious: this.reportForm.value.dateRange.dateStartPrevious,
      dateEndPrevious: this.reportForm.value.dateRange.dateEndPrevious,
      department: this.reportForm.value.department === -1 ? null : this.reportForm.value.department,
      attendant: this.reportForm.value.attendant === -1 ? null : this.reportForm.value.attendant,
      channel: this.reportForm.value.channel === -1 ? null : this.reportForm.value.channel
    };
    this.getCountContactItems(formsValue);
    this.getContactListItems(formsValue);
    this.getGraphicChannelContact(formsValue);
    this.getGraphicAttendantContact(formsValue);
    this.getGraphicDepartmentContact(formsValue);
  }
  private createForm(): void {
    const today = new Date();
    const yesterday = new Date(today);
    const todayDay = today.getDate();
    const todayMonth = today.getMonth();
    const todayYear = today.getFullYear();

    const lastMonthSameDay = new Date(todayYear, todayMonth - 1, todayDay);
    const lastMonthPreviousDay = new Date(todayYear, todayMonth - 1, todayDay - 1);

    yesterday.setDate(today.getDate() - 1);
    this.reportForm = this.fb.group({
      dateRange: this.fb.group({
        dateStartCurrent: [yesterday, []],
        dateEndCurrent: [today, []],
        dateStartPrevious: [lastMonthPreviousDay, []],
        dateEndPrevious: [lastMonthSameDay, []],
      }),
      department: [-1],
      attendant: [-1],
      channel: [-1]
    });
  }

  onFormChange(changes: any) {
    if (changes.dateRange.dateStartCurrent && changes.dateRange.dateEndCurrent ||
      changes.dateRange.dateStartPrevious && changes.dateRange.dateEndPrevious) {
      const formsValue = {
        dateStartCurrent: this.reportForm.value.dateRange.dateStartCurrent,
        dateEndCurrent: this.reportForm.value.dateRange.dateEndCurrent,
        dateStartPrevious: this.reportForm.value.dateRange.dateStartPrevious,
        dateEndPrevious: this.reportForm.value.dateRange.dateEndPrevious,
        department: changes.department === -1 ? null : changes.department,
        attendant: changes.attendant === -1 ? null : changes.attendant,
        channel: changes.channel === -1 ? null : changes.channel
      }

      this.getCountContactItems(formsValue);
      this.getContactListItems(formsValue);
      this.getGraphicChannelContact(formsValue);
      this.getGraphicAttendantContact(formsValue);
      this.getGraphicDepartmentContact(formsValue);
    }
  }

  loadPage(page: number) {
    this.pager.page = page;
    const formsValue = {
      dateStartCurrent: this.reportForm.value.dateRange.dateStartCurrent,
      dateEndCurrent: this.reportForm.value.dateRange.dateEndCurrent,
      dateStartPrevious: this.reportForm.value.dateRange.dateStartPrevious,
      dateEndPrevious: this.reportForm.value.dateRange.dateEndPrevious,
      department: this.reportForm.value.department === -1 ? null : this.reportForm.value.department,
      attendant: this.reportForm.value.attendant === -1 ? null : this.reportForm.value.attendant,
      channel: this.reportForm.value.channel === -1 ? null : this.reportForm.value.channel,
    };
    this.getContactListItems(formsValue);
  }

  onPeriodChangeCurrent(event: any) {
    const today = new Date();
    switch (event.value) {
      case '1':
        this.reportForm.get('dateRange').patchValue({
          dateStartCurrent: new Date(),
          dateEndCurrent: new Date()
        });
        this.picker.close();
        break;
      case '7':
        this.reportForm.get('dateRange').patchValue({
          dateStartCurrent: new Date(today.setDate(today.getDate() - 7)),
          dateEndCurrent: new Date()
        });
        this.picker.close();
        break;
      case '15':
        this.reportForm.get('dateRange').patchValue({
          dateStartCurrent: new Date(today.setDate(today.getDate() - 15)),
          dateEndCurrent: new Date()
        });
        this.picker.close();
        break;
      case '30':
        this.reportForm.get('dateRange').patchValue({
          dateStartCurrent: new Date(today.setDate(today.getDate() - 30)),
          dateEndCurrent: new Date()
        });
        this.picker.close();
        break;
      case '60':
        this.reportForm.get('dateRange').patchValue({
          dateStartCurrent: new Date(today.setDate(today.getDate() - 60)),
          dateEndCurrent: new Date()
        });
        this.picker.close();
        break;
    }
  }

  onPeriodChangePrevious(event: any) {
    const today = new Date();
    switch (event.value) {
      case '1':
        this.reportForm.get('dateRange').patchValue({
          dateStartPrevious: new Date(),
          dateEndPrevious: new Date()
        });
        this.picker.close();
        break;
      case '7':
        this.reportForm.get('dateRange').patchValue({
          dateStartPrevious: new Date(today.setDate(today.getDate() - 7)),
          dateEndPrevious: new Date()
        });
        this.picker.close();
        break;
      case '15':
        this.reportForm.get('dateRange').patchValue({
          dateStartPrevious: new Date(today.setDate(today.getDate() - 15)),
          dateEndPrevious: new Date()
        });
        this.picker.close();
        break;
      case '30':
        this.reportForm.get('dateRange').patchValue({
          dateStartPrevious: new Date(today.setDate(today.getDate() - 30)),
          dateEndPrevious: new Date()
        });
        this.picker.close();
        break;
      case '60':
        this.reportForm.get('dateRange').patchValue({
          dateStartPrevious: new Date(today.setDate(today.getDate() - 60)),
          dateEndPrevious: new Date()
        });
        this.picker.close();
        break;
    }
  }


  getByUserAttendant() {
    this.userSerivce.findByAttendant().subscribe({
      next: (users: User[]) => {
        this.userAttendant = users;
      },
      complete: () => this.loadingContent = false,
      error: (err) => this.alertService.error(err.error.message)
    })
  }

  getDepartmentByCompany() {
    this.departmentService.getList().subscribe({
      next: (dresponseDepart: Department[]) => {
        this.department = dresponseDepart;
      },
      complete: () => this.loadingContent = false,
      error: (err) => this.alertService.error(err.error.message)
    })
  }

  getByAllChannel() {
    this.channelService.getByAllChannel().subscribe({
      next: (channel: Channel[]) => {
        this.channelAttendant = channel;
      },
      complete: () => this.loadingContent = false,
      error: (err) => this.alertService.error(err.error.message)
    })
  }

  getCountContactItems(form) {
    this.reportService.getCountContactItems(form).subscribe({
      next: (reportCount: ResponseCountItemsContactTypes) => {
        this.countItemsContact = reportCount;
      },
      complete: () => this.loadingContent = false,
      error: (err) => this.alertService.error(err.error.message)
    })
  }

  getContactListItems(form) {
    this.reportService.getAttendanceItemsContact({ ...form, page: this.pager.page, limit: this.pager.perPage }).subscribe({
      next: (reportList) => {
        this.pager = reportList;
      },
      complete: () => this.loadingContent = false,
      error: (err) => this.alertService.error(err.error.message)
    })
  }

  getGraphicChannelContact(form) {
    this.reportService.getGraphicChannelContact(form).subscribe({
      next: (graphicChannel: ResponseGraphicChannelTypes[]) => {
        this.graphicChannel = graphicChannel.map((item) => {
          return {
            name: item.channel,
            value: item.newContacts
          }
        });
      },
      complete: () => this.loadingContent = false,
      error: (err) => this.alertService.error(err.error.message)
    })
  }

  getGraphicAttendantContact(form) {
    this.reportService.getGraphicAttendantContact(form).subscribe({
      next: (graphicUser: ResponseGraphicUserTypes[]) => {
        this.graphicUser = graphicUser.map((item) => {
          return {
            name: item.attendant ? item.attendant : '',
            value: item.totalNewContacts
          }
        });
      },
      complete: () => this.loadingContent = false,
      error: (err) => this.alertService.error(err.error.message)
    })
  }

  getGraphicDepartmentContact(form) {
    this.reportService.getGraphicDepartmentContact(form).subscribe({
      next: (graphicDepartment: ResponseGraphicDepartmentTypes[]) => {
        this.graphicDepartment = graphicDepartment.map((item) => {
          return {
            name: item.department,
            value: item.newContacts
          }
        });
      },
      complete: () => this.loadingContent = false,
      error: (err) => this.alertService.error(err.error.message)
    })
  }

  exportFile() {
    const formsValue = {
      dateStartCurrent: this.reportForm.value.dateRange.dateStartCurrent,
      dateEndCurrent: this.reportForm.value.dateRange.dateEndCurrent,
      dateStartPrevious: this.reportForm.value.dateRange.dateStartPrevious,
      dateEndPrevious: this.reportForm.value.dateRange.dateEndPrevious,
      department: this.reportForm.value.department === -1 ? null : this.reportForm.value.department,
      attendant: this.reportForm.value.attendant === -1 ? null : this.reportForm.value.attendant,
      channel: this.reportForm.value.channel === -1 ? null : this.reportForm.value.channel,
    };
    this.reportService.getItemsContactExport(formsValue).subscribe({
      next: async (exportContact) => {
        const bufferExport = await this.exportService.generateExcelNewContact(exportContact);
        const blob = new Blob([bufferExport], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });

        saveAs(blob, 'relatorio-new-contact.xlsx');
      },
      complete: () => this.loadingContent = false,
      error: (err) => this.alertService.error(err.error.message)
    })
  }


  onChannelSelect(value: any, label: string = 'Canal'): void {
    this.reportForm.get('channel')?.setValue(value);
    this.labelChannel = label;
  }

  onDepartmentSelect(value: any, label: string = 'Departamento'): void {
    this.reportForm.get('department')?.setValue(value);
    this.labelDepartment = label;
  }

  onAttendantSelect(value: any, label: string = 'Atendante'): void {
    this.reportForm.get('attendant')?.setValue(value);
    this.labelAttendant = label;
  }

  onGraphicSelect(value: any, label: string = 'report.listContact.chart'): void {
    this.selectedGraphic = value;
    this.selectedGraphicLabel = label;
  }

  onListSelect(value: any, label: string = 'report.listContact.list'): void {
    this.selectedList = value;
    this.selectedListLabel = label;
  }

  openModalCountContact(type: string, data?: any[]): void {
    const refBottom = this.dialog.open(ModalReportContactComponent, {
      width: '70rem', height: '40rem', data: {
        type,
        data,
        dateStartCurrent: this.reportForm.value.dateRange.dateStartCurrent,
        dateEndCurrent: this.reportForm.value.dateRange.dateEndCurrent,
        dateStartPrevious: this.reportForm.value.dateRange.dateStartPrevious,
        dateEndPrevious: this.reportForm.value.dateRange.dateEndPrevious,
        department: this.reportForm.value.department === -1 ? null : this.reportForm.value.department,
        attendant: this.reportForm.value.attendant === -1 ? null : this.reportForm.value.attendant,
        channel: this.reportForm.value.channel === -1 ? null : this.reportForm.value.channel
      }
    });

    refBottom.afterClosed();
  };
}
